import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  InspectionReport,
  InspectionReportNew,
  InspectionReportUpdate,
  InspectionReportUpdateLocations,
  InspectionReportUpdateTexts,
  InspectionReportList,
  InspectionReportPdfUrl,
  PdfOptions,
  ReportStatus,
  InspectionReportReuse,
  PastReportVersion,
  InspectionReportUri,
  InspectionReportMetaData
} from "../../_objects/inspection-report";
import { PaperKind, PDF_COMPRESS_PARAMS_HIGH, PDF_COMPRESS_PARAMS_LOW, PDF_COMPRESS_PARAMS_MEDIUM } from "../../app.constants";
import { SubjectFilter } from "../storage.service";
import { ApiService } from "../base/api-base.service";
import { InspectionReportContractService } from "../interfaces/inspection-report-contract.service";
import { NetworkStatusService } from "../network-status.service";
import { InspectionReportOfflineService } from "../offline/inspection-report-offline.service";
import { InspectionReportOnlineService } from "../online/inspection-report-online.service";
import { MeasurementModeName } from "../../_objects/measurement-mode-name";

@Injectable()
export class InspectionReportService extends ApiService<InspectionReportContractService> implements InspectionReportContractService {
  constructor(
    protected networkStatusService: NetworkStatusService,
    protected offlineService: InspectionReportOfflineService,
    protected onlineService: InspectionReportOnlineService
  ) {
    super(networkStatusService, offlineService, onlineService);
  }

  // RP01 GET /api/v1/reports
  getInspectionReports(
    inspectionId?: string,
    statusCode?: string,
    subjectFilter?: SubjectFilter,
    isOwn?: boolean,
    sort?: { active: string; direction: string },
    page?: { index: number; size: number },
    searchText?: string
  ): Observable<InspectionReportList> {
    return this.service.getInspectionReports(inspectionId, statusCode, subjectFilter, isOwn, sort, page, searchText);
  }

  // RP02 GET /api/v1/reports/{reportId}
  getInspectionReport(reportId: string, version: number = null): Observable<InspectionReport> {
    return this.service.getInspectionReport(reportId, version);
  }

  // RP03 POST /api/v1/reports
  createInspectionReport(inspectionReport: InspectionReportNew): Observable<any> {
    return this.service.createInspectionReport(inspectionReport);
  }

  // RP04 PUT /api/v1/reports/{reportId}
  updateInspectionReport(reportId: string, inspectionReportUpdate: InspectionReportUpdate): Observable<InspectionReport> {
    return this.service.updateInspectionReport(reportId, inspectionReportUpdate);
  }

  // RP05 DELETE /api/v1/reports/{reportId}
  deleteInspectionReport(reportId: string): Observable<any> {
    return this.service.deleteInspectionReport(reportId);
  }

  // RP06 GET /api/v1/reports/related/inspections
  getInspectionReportsByInspectionIds(inspectionIds: string[], size: number, index?: number): Observable<InspectionReportList> {
    return this.service.getInspectionReportsByInspectionIds(inspectionIds, size, index);
  }

  // RP07 PUT /api/v1/reports/{reportId}/reportLocations
  patchInspectionReportLocations(inspectionReportUpdateLocations: InspectionReportUpdateLocations): Observable<any> {
    return this.service.patchInspectionReportLocations(inspectionReportUpdateLocations);
  }

  // RP08 PUT /api/v1/reports/{reportId}/editableTexts
  patchInspectionReportTexts(inspectionReportUpdateTexts: InspectionReportUpdateTexts): Observable<any> {
    return this.service.patchInspectionReportTexts(inspectionReportUpdateTexts);
  }

  // RP09 PUT /api/v1/reports/{reportId}/status
  patchInspectionReportStatus(reportStatus: ReportStatus, measurementModeName: MeasurementModeName): Observable<InspectionReport> {
    return this.service.patchInspectionReportStatus(reportStatus, measurementModeName);
  }

  // RP10 PUT /api/v1/reports/{reportId}/editableTexts/import
  importInspectionReportTexts(inspectionReport: InspectionReport, file: File, fileType: string): Observable<InspectionReport> {
    return this.service.importInspectionReportTexts(inspectionReport, file, fileType);
  }

  // RP11 GET /api/v1/reports/{reportId}/pdf
  getInspectionReportPdf(reportId: string, version?: number, preview?: boolean): Observable<Blob> {
    return this.service.getInspectionReportPdf(reportId, version, preview);
  }

  // RP12 GET /api/v1/reports/{reportId}/pdf/url
  getInspectionReportPdfUrl(reportId: string, version?: string, preview?: boolean, fileName?: string): Observable<InspectionReportPdfUrl> {
    return this.service.getInspectionReportPdfUrl(reportId, version, preview, fileName);
  }

  // RP13 POST /api/v1/reports/{reportId}/pdf
  createInspectionReportPdf(
    reportId: string,
    pdfOptions: PdfOptions,
    measurementModeName: MeasurementModeName,
    preview?: boolean
  ): Observable<InspectionReportPdfUrl> {
    return this.service.createInspectionReportPdf(reportId, pdfOptions, measurementModeName, preview);
  }

  /** RP14 �������|�[�g�̕ҏW�\�e�L�X�g�𑼃��|�[�g����ė��p���X�V
   * PUT /api/v1/reports/{reportId}/editableTexts/import/reusedata
   * @param  {InspectionReportReuse} reuseRequest
   * @returns Observable
   */
  putInspectionReportReuse(reuseRequest: InspectionReportReuse): Observable<InspectionReport> {
    return this.service.putInspectionReportReuse(reuseRequest);
  }

  // RP15 GET /api/v1/reports/{reportId}/share
  getInspectionReportSharedLink(reportId: string, expiryDate: string, version?: number, isPreview?: boolean): Observable<InspectionReportUri> {
    return this.service.getInspectionReportSharedLink(reportId, expiryDate, version, isPreview);
  }

  // RP015 POST /api/v1/reports/{reportId}
  createNewVersionInspectionReport(reportId: string): Observable<any> {
    return this.service.createNewVersionInspectionReport(reportId);
  }

  // RP16 GET /api/v1/reports/{reportId}/history
  getInspectionReportVersion(reportId: string): Observable<PastReportVersion[]> {
    return this.service.getInspectionReportVersion(reportId);
  }

  // RP17 POST /api/v1/reports/{reportId}/pdf/async
  createReportPdfAsync(
    reportId: string,
    pdfOptions: PdfOptions,
    measurementModeName: MeasurementModeName,
    preview?: boolean,
    previewPdfHtml?: string
  ): Observable<InspectionReport> {
    return this.service.createReportPdfAsync(reportId, pdfOptions, measurementModeName, preview, previewPdfHtml);
  }

  // RP18 POST api/reports/{reportId}/thumbnail
  createReportThumbnail(reportId: string, pdfOptions: PdfOptions, measurementModeName: MeasurementModeName): Observable<any> {
    return this.service.createReportThumbnail(reportId, pdfOptions, measurementModeName);
  }

  // RP19 PUT api/reports/{reportId}/thumbFilePath
  setUnavailableThumbFilePath(reportId: string): Observable<any> {
    return this.service.setUnavailableThumbFilePath(reportId);
  }

  // RPH1 GET /api/v1/reports/{reportId}/preview
  getInspectionReportPreview(reportId: string, measurementModeName: MeasurementModeName, firstPageOnly = false): Observable<string> {
    return this.service.getInspectionReportPreview(reportId, measurementModeName, firstPageOnly);
  }

  // RP20 POST /api/reports/metadata
  getReportsMetaDataForInspections(inspectionIds: string[]): Observable<InspectionReportMetaData[]> {
    return this.service.getReportsMetaDataForInspections(inspectionIds);
  }

  // ----------------------------------------
  convertInspectionReport2InspectionReportUpdateLocations(inspectionReport: InspectionReport): InspectionReportUpdateLocations {
    const inspectionReportUpdateLocations = new InspectionReportUpdateLocations();
    inspectionReportUpdateLocations.reportId = inspectionReport.reportId;
    inspectionReportUpdateLocations.reportLocations = inspectionReport.reportLocations;

    return inspectionReportUpdateLocations;
  }

  convertInspectionReport2InpsctionReportUpdate(inspectionReport: InspectionReport): InspectionReportUpdate {
    const inspectionReportUpdate = new InspectionReportUpdate();

    inspectionReportUpdate.reportId = inspectionReport.reportId;
    if (inspectionReport.title) {
      inspectionReportUpdate.title = inspectionReport.title.trim();
    } else {
      inspectionReportUpdate.title = null;
    }
    inspectionReportUpdate.templateId = inspectionReport.templateId;
    inspectionReportUpdate.inspectionIds = inspectionReport.inspectionIds;
    inspectionReportUpdate.reportLocations = inspectionReport.reportLocations;
    inspectionReportUpdate.editableTexts = inspectionReport.editableTexts;
    inspectionReportUpdate.status = inspectionReport.status;
    inspectionReportUpdate.isAutoInputTitle = inspectionReport.isAutoInputTitle;
    inspectionReportUpdate.schemaVersion = inspectionReport.schemaVersion;
    inspectionReportUpdate.draftQuality = inspectionReport.draftQuality;
    inspectionReportUpdate.finalQuality = inspectionReport.finalQuality;

    return inspectionReportUpdate;
  }

  convertInspectionReport2InspectionReportUpdateTexts(inspectionReport: InspectionReport): InspectionReportUpdateTexts {
    const inspectionReportUpdateTexts: InspectionReportUpdateTexts = new InspectionReportUpdateTexts();

    inspectionReportUpdateTexts.reportId = inspectionReport.reportId;
    inspectionReportUpdateTexts.editableTexts = inspectionReport.editableTexts;
    inspectionReportUpdateTexts.excludedInputGroups = inspectionReport.excludedInputGroups;

    return inspectionReportUpdateTexts;
  }

  getPdfOptions(quality: number, isDraft: boolean, paperSize?: string): PdfOptions {
    const pdfOptions = new PdfOptions();

    pdfOptions.quality = PDF_COMPRESS_PARAMS_MEDIUM.QUALITY;
    pdfOptions.imageQuality = PDF_COMPRESS_PARAMS_MEDIUM.QUALITY;
    if (isDraft) {
      if (quality === PDF_COMPRESS_PARAMS_HIGH.KEY) {
        pdfOptions.dpi = PDF_COMPRESS_PARAMS_HIGH.DPI;
        pdfOptions.imageDpi = PDF_COMPRESS_PARAMS_HIGH.DPI;
      } else if (quality === PDF_COMPRESS_PARAMS_LOW.KEY) {
        pdfOptions.dpi = PDF_COMPRESS_PARAMS_LOW.DPI;
        pdfOptions.imageDpi = PDF_COMPRESS_PARAMS_LOW.DPI;
      } else {
        pdfOptions.dpi = PDF_COMPRESS_PARAMS_MEDIUM.DPI;
        pdfOptions.imageDpi = PDF_COMPRESS_PARAMS_MEDIUM.DPI;
      }
    } else {
      if (quality === PDF_COMPRESS_PARAMS_HIGH.KEY) {
        pdfOptions.dpi = PDF_COMPRESS_PARAMS_HIGH.DPI;
        pdfOptions.imageDpi = PDF_COMPRESS_PARAMS_HIGH.DPI;
      } else if (quality === PDF_COMPRESS_PARAMS_LOW.KEY) {
        pdfOptions.dpi = PDF_COMPRESS_PARAMS_LOW.DPI;
        pdfOptions.imageDpi = PDF_COMPRESS_PARAMS_LOW.DPI;
      } else {
        pdfOptions.dpi = PDF_COMPRESS_PARAMS_MEDIUM.DPI;
        pdfOptions.imageDpi = PDF_COMPRESS_PARAMS_MEDIUM.DPI;
      }
    }

    switch (paperSize) {
      case "A4":
        pdfOptions.PageSize = PaperKind.A4;
        break;
      case "B5":
        pdfOptions.PageSize = PaperKind.B5;
        break;
      case "B5JIS":
        pdfOptions.PageSize = PaperKind.B5JIS;
        break;
      case "Letter":
        pdfOptions.PageSize = PaperKind.Letter;
        break;
      case "Legal":
        pdfOptions.PageSize = PaperKind.Legal;
        break;
      default:
        pdfOptions.PageSize = PaperKind.Letter;
        break;
    }

    return pdfOptions;
  }

  convertInspectionReport2InpsctionReportStatus(quality: number, inspectionReport: InspectionReport, paperSize?: string): ReportStatus {
    const inspectionReporStatus = new ReportStatus();

    inspectionReporStatus.reportId = inspectionReport.reportId;
    inspectionReporStatus.status = inspectionReport.status;
    inspectionReporStatus.pdfOptions = this.getPdfOptions(quality, false, paperSize);

    return inspectionReporStatus;
  }
}
